import { useState, useEffect, useRef } from 'react';

/**
 * A custom React hook to detect user inactivity and trigger a logout.
 * @param {function} onIdle - The function to call when the user becomes idle (e.g., logout).
 * @param {number} idleTimeout - The total time in milliseconds before the user is considered idle.
 * @param {number} promptTimeout - The time in milliseconds to show a warning prompt before logging out.
 */
export default function useIdleTimer({ onIdle, idleTimeout = 15 * 60 * 1000, promptTimeout = 60 * 1000 }) {
    const [isPromptVisible, setIsPromptVisible] = useState(false);
    const idleTimer = useRef(null);
    const promptTimer = useRef(null);

    const events = ['mousemove', 'keydown', 'click', 'scroll'];

    const startIdleTimer = () => {
        clearTimeout(idleTimer.current);
        clearTimeout(promptTimer.current);
        
        idleTimer.current = setTimeout(() => {
            setIsPromptVisible(true); // Show the warning modal
            promptTimer.current = setTimeout(() => {
                onIdle(); // The prompt time has expired, so log the user out
            }, promptTimeout);
        }, idleTimeout - promptTimeout);
    };

    const resetTimer = () => {
        // User is active, hide the prompt and restart the main idle timer
        setIsPromptVisible(false);
        startIdleTimer();
    };

    const stayActive = () => {
        // User clicked the "Stay Logged In" button in the modal
        resetTimer();
    };

    useEffect(() => {
        // Start the timer when the hook is first used
        startIdleTimer();

        // Add event listeners to track user activity
        events.forEach(event => window.addEventListener(event, resetTimer));

        // Cleanup function to remove event listeners when the component unmounts
        return () => {
            clearTimeout(idleTimer.current);
            clearTimeout(promptTimer.current);
            events.forEach(event => window.removeEventListener(event, resetTimer));
        };
    }, []); // Only run this effect once

    return { isPromptVisible, stayActive };
}
